import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { financialService, StudentOption } from "../services/financialService";

/** Why a learner found here is not in class: they can still owe, and pay. */
const statusLabel = (status: string) =>
  status === "inactive" ? "record closed" : `${status}, not in school`;

/** Search-as-you-type student picker. Avoids fetching the entire student
 * roster client-side (1,800+ rows in production data made that a ~90-request
 * page load) by hitting /students/service-directory/?search= as the user
 * types. Renders a real <select name={name}> so it keeps working with
 * uncontrolled FormData-based submit handlers. */
export default function StudentSearchField({
  name = "student",
  required = true,
  className = "input",
  onSelect,
}: {
  name?: string;
  required?: boolean;
  className?: string;
  /** Optional: also notified with the full StudentOption (or null) on every
   * selection change, for callers that need the id outside of form submit. */
  onSelect?: (student: StudentOption | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<StudentOption | null>(null);
  const container = useRef<HTMLDivElement>(null);

  // The <select> below is controlled, so form.reset() cannot clear it -- React
  // simply re-renders the previous selection. Listening for the form's own
  // reset keeps the picker in step with the rest of the form it sits in,
  // rather than every caller having to remember to clear it.
  useEffect(() => {
    const form = container.current?.closest("form");
    if (!form) return undefined;
    const clear = () => {
      setSelected(null);
      setSearch("");
      onSelect?.(null);
    };
    form.addEventListener("reset", clear);
    return () => form.removeEventListener("reset", clear);
  }, [onSelect]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const results = useQuery({
    queryKey: ["financial-student-search", debouncedSearch],
    queryFn: () => financialService.searchStudents(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  });

  const matches = results.data ?? [];
  const options = selected && !matches.some((s) => s.id === selected.id)
    ? [selected, ...matches]
    : matches;

  return (
    <div ref={container}>
      <input
        type="text"
        className={className}
        placeholder="Search by name, ID number, or registration number…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <select
        required={required}
        name={name}
        className={`${className} mt-1`}
        value={selected?.id ?? ""}
        onChange={(event) => {
          const match = options.find((s) => String(s.id) === event.target.value) ?? null;
          setSelected(match);
          onSelect?.(match);
        }}
      >
        <option value="">
          {debouncedSearch.length < 2
            ? "Type to search…"
            : results.isFetching
              ? "Searching…"
              : "Select a student…"}
        </option>
        {options.map((s) => (
          <option key={s.id} value={s.id}>
            {s.full_name} ({s.student_id}
            {s.admission_number ? ` · Reg. ${s.admission_number}` : ""})
            {s.status && s.status !== "active" ? ` · ${statusLabel(s.status)}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
